/**
 * API client for the Gevelsteenbedrijf Workers API.
 * All fetch calls go through here so the base URL is in one place.
 */

export const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  'https://gevelsteenbedrijf-api-v5.franky-f29.workers.dev';

/** Session token stored in localStorage (mirrors the httpOnly cookie set by the API) */
const SESSION_KEY = 'gevel_session_token';

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_KEY, token);
  } catch { /* ignore */ }
}

export function clearSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: extraHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string> | undefined),
  };

  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    credentials: 'include', // send httpOnly cookie if same-origin or CORS allows
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const e = err as { error?: unknown; message?: unknown; details?: unknown };

    // Bouw een leesbare error-tekst — vermijdt "[object Object]" wanneer de
    // API een Zod-issues-array of geneste error doorstuurt.
    let msg: string;
    if (typeof e.error === 'string') {
      msg = e.error;
    } else if (typeof e.message === 'string') {
      msg = e.message;
    } else if (Array.isArray(e.details) && e.details.length > 0) {
      // Zod-validatie: pak de eerste issue zijn message/path
      const first = e.details[0] as { message?: string; path?: (string | number)[] };
      const path = first.path?.length ? `${first.path.join('.')}: ` : '';
      msg = `${path}${first.message ?? 'Validation error'}`;
    } else if (e.error && typeof e.error === 'object') {
      // Zod via Hono kan `error: { issues: [...] }` retourneren
      const inner = e.error as { issues?: { message?: string; path?: (string | number)[] }[]; message?: string };
      if (Array.isArray(inner.issues) && inner.issues.length > 0) {
        const first = inner.issues[0];
        const path = first.path?.length ? `${first.path.join('.')}: ` : '';
        msg = `${path}${first.message ?? 'Validation error'}`;
      } else if (typeof inner.message === 'string') {
        msg = inner.message;
      } else {
        msg = `Server error (${res.status})`;
      }
    } else {
      msg = res.statusText || `HTTP ${res.status}`;
    }

    throw Object.assign(new Error(msg), { status: res.status });
  }

  return res.json() as Promise<T>;
}

// ─── Products ─────────────────────────────────────────────────────────────────

export interface ApiProduct {
  id: string;
  slug: string;
  name: string;
  color: string;
  color_hex: string;
  texture: string;
  category: string;
  dimensions: string;
  weight: string;
  description_nl: string;
  supplier_code: string;
  supplier_id: string | null;
  sort_order: number;
  is_active: number;
  primary_image: string | null;
  // Catalog price (used as fallback for discount calculations on the front-end)
  locked_price?: number | null;
  /** v5 blok 4: levertermijn in weken, zichtbaar op de offerte. */
  delivery_weeks?: number | null;
  /** v5 blok 4/9: gevelsteen of toebehoren (lijm/voegsel/profielen). */
  product_type?: string;
  /** v5 blok 9: productnummer (P-1001 …). */
  serial_number?: string | null;
  /** v5 blok 9: technische fiche van de fabrikant (PDF-URL). */
  fiche_url?: string | null;
  // ─── Discount badges (front-of-card marketing) ───────────────────────────
  show_discount_badge?: number;          // 0 | 1
  discount_pct?: number;                 // 0–50
  discount_label?: string | null;
  liquidation_active?: number;           // 0 | 1
  liquidation_pct?: number;              // 0–50
  liquidation_label?: string | null;
  liquidation_start?: number | null;     // unix ts
  liquidation_end?: number | null;       // unix ts
}

export async function fetchProducts(params?: { category?: string; q?: string }): Promise<ApiProduct[]> {
  const qs = params
    ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as [string, string][]).toString()
    : '';
  const res = await request<{ ok: boolean; data: ApiProduct[] }>(`/api/products${qs}`);
  return res.data;
}

// ─── Calculator ───────────────────────────────────────────────────────────────

export interface CalcRequest {
  productId: string;
  surfaceM2: number;
  jointWidth?: number;   // mm, default 12
  cuttingLoss?: number;  // fraction 0–0.5, default 0.10
  corners?: number;
  includeGlue?: boolean;
  includeGrout?: boolean;
}

export interface CalcResponse {
  ok: boolean;
  data: {
    product: {
      id: string;
      name: string;
      slug: string;
      dimensions: string;
      parsedDimensions: { lengthMm: number; heightMm: number };
    };
    input: {
      surfaceM2: number;
      jointWidthMm: number;
      cuttingLossPct: number;
      corners: number;
    };
    stones: {
      stonesPerM2: number;
      effectiveSurfaceM2: number;
      stonesNeeded: number;
    };
    pricing: {
      supplierPricePerM2: number | null;
      marginPct: number;
      customerPricePerM2: number | null;
      stonesCostExcl: number | null;
    };
    accessories: {
      glue: { bags: number; pricePerBag: number; total: number };
      grout: { bags: number; pricePerBag: number; total: number };
    };
    totals: {
      totalExclVat: number | null;
      vatAmount: number | null;
      totalInclVat: number | null;
      vatRate: number;
    };
  };
}

export async function calculateMaterials(req: CalcRequest): Promise<CalcResponse['data']> {
  const res = await request<CalcResponse>('/api/calculator/calculate', {
    method: 'POST',
    body: req,
  });
  return res.data;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string | null;
  company_name?: string | null;
  btw_number?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  notes?: string | null;
}

export interface RegisterPayload {
  email: string;
  name: string;
  phone?: string;
  company_name?: string;
  btw_number?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  notes?: string;
}

export async function authRegister(data: RegisterPayload): Promise<void> {
  await request('/api/auth/register', { method: 'POST', body: data });
}

export async function authUpdateProfile(data: Partial<Omit<RegisterPayload, 'email'>>): Promise<ApiUser> {
  const res = await request<{ ok: boolean; user: ApiUser }>('/api/auth/profile', {
    method: 'PUT',
    body: data,
  });
  return res.user;
}

export async function authRequestCode(email: string): Promise<void> {
  await request('/api/auth/request-code', { method: 'POST', body: { email } });
}

export async function authVerifyCode(email: string, code: string): Promise<{ user: ApiUser; token: string }> {
  const res = await request<{ ok: boolean; token: string; user: ApiUser }>(
    '/api/auth/verify-code',
    { method: 'POST', body: { email, code } }
  );
  setSessionToken(res.token);
  return { user: res.user, token: res.token };
}

/** v5 blok 7: klassieke paswoord-login (naast code-per-mail). */
export async function authLoginPassword(email: string, password: string): Promise<{ user: ApiUser; token: string }> {
  const res = await request<{ ok: boolean; token: string; user: ApiUser }>(
    '/api/auth/login-password',
    { method: 'POST', body: { email, password } }
  );
  setSessionToken(res.token);
  return { user: res.user, token: res.token };
}

/** v5 blok 7: eigen paswoord instellen/wijzigen (ingelogd). */
export async function authSetPassword(newPassword: string, loginMethod: 'code' | 'password' | 'both' = 'both'): Promise<void> {
  await request('/api/auth/set-password', { method: 'POST', body: { new_password: newPassword, login_method: loginMethod } });
}

/** v5 blok 7: admin zet paswoord voor een gebruiker (bv. nieuw reseller-account). */
export async function adminSetUserPassword(userId: string, password: string, loginMethod: 'code' | 'password' | 'both' = 'password'): Promise<void> {
  await request(`/api/admin/users/${encodeURIComponent(userId)}/password`, { method: 'PUT', body: { password, login_method: loginMethod } });
}

// ─── Register + Quote (inline calculator flow) ───────────────────────────────

export type InstallerChoice = 'self' | 'friend' | 'installer';

export interface RegisterAndQuotePayload {
  name: string;
  email: string;
  phone: string;
  address: string;
  postal_code_city: string;
  is_b2b: boolean;
  company_name?: string;
  btw_number?: string;
  quote_total: number;
  quote_notes: string;
  /** v5 blok 4: meerdere gevelstenen per offerte — één regel per steen. */
  items?: { product_id: string; qty_m2: number; notes?: string | null }[];
  /** Wie installeert de baksteenstrips? Optioneel — null = niet gevraagd. */
  installer_choice?: InstallerChoice | null;
  installateur_ids?: string[] | null;
  eigen_groothandel?: EigenGroothandel | null;
  project_termijn?: '3m' | '6m' | '1j' | 'meer' | 'info' | null;
  plaatsing?: 'zelf' | 'installateur' | 'groothandel' | null;
}

export interface RegisterAndQuoteResponse {
  ok: boolean;
  user: ApiUser;
  quote_id: string;
  token: string;
  is_new: boolean;
}

// ─── V6 punten 12-17: installateurs in de regio ───────────────────────────
export interface Installateur {
  id: string;
  company_name: string;
  city: string | null;
  postal_code: string | null;
  contact_phone: string | null;
  website: string | null;
  logo_url: string | null;
  type: string;
  afstand_km: number;
}

export async function getInstallateurs(postcode: string): Promise<Installateur[]> {
  const res = await request<{ ok: boolean; data: { installateurs: Installateur[] } }>(
    `/api/installateurs?postcode=${encodeURIComponent(postcode)}`
  );
  return res.data.installateurs;
}

export interface EigenGroothandel { naam: string; plaats?: string; email?: string }

export async function registerAndQuote(payload: RegisterAndQuotePayload): Promise<RegisterAndQuoteResponse> {
  const res = await request<RegisterAndQuoteResponse>('/api/auth/register-and-quote', {
    method: 'POST',
    body: payload,
  });
  setSessionToken(res.token);
  return res;
}

export async function authMe(): Promise<ApiUser | null> {
  try {
    const res = await request<{ ok: boolean; user: ApiUser }>('/api/auth/me');
    return res.user;
  } catch {
    return null;
  }
}

export async function authLogout(): Promise<void> {
  try {
    await request('/api/auth/logout', { method: 'POST' });
  } catch { /* ignore */ }
  clearSessionToken();
}

// ─── Quotes ───────────────────────────────────────────────────────────────────

export interface CreateQuotePayload {
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  notes_nl?: string;
  total_amount?: number;
  /** v5 blok 4: meerdere gevelstenen per offerte — één regel per steen. */
  items?: { product_id: string; qty_m2: number; notes?: string | null }[];
  installer_choice?: InstallerChoice | null;
  installateur_ids?: string[] | null;
  eigen_groothandel?: EigenGroothandel | null;
  project_termijn?: '3m' | '6m' | '1j' | 'meer' | 'info' | null;
  plaatsing?: 'zelf' | 'installateur' | 'groothandel' | null;
}

export interface ApiQuote {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  status: string;
  total_excl_vat: number;
  total_incl_vat: number;
  vat_rate: number;
  is_b2b?: number;
  customer_btw_number?: string | null;
  notes_nl: string | null;
  items_count: number;
  created_at: number;
  // Plaatsing
  wants_installer?: number;
  installer_choice?: InstallerChoice | null;
  installateur_ids?: string[] | null;
  eigen_groothandel?: EigenGroothandel | null;
  project_termijn?: '3m' | '6m' | '1j' | 'meer' | 'info' | null;
  plaatsing?: 'zelf' | 'installateur' | 'groothandel' | null;
  // Reseller info (alleen gevuld als de offerte naar een doorverkoper ging)
  reseller_id?: string | null;
  reseller_company_name?: string | null;
  reseller_logo_url?: string | null;
  reseller_website?: string | null;
  reseller_contact_phone?: string | null;
  reseller_contact_email?: string | null;
  reseller_installers_json?: string | null;
}

export async function createQuote(payload: CreateQuotePayload): Promise<ApiQuote> {
  const res = await request<{ ok: boolean; data: ApiQuote }>('/api/quotes', {
    method: 'POST',
    body: payload,
  });
  return res.data;
}

// ─── Admin: Stats ─────────────────────────────────────────────────────────────

export interface AdminStats {
  products: number;
  suppliers: number;
  customers: number;
  quotes: { total: number; draft: number; sent: number; accepted: number };
  revenue_excl_vat: number;
}

export async function adminGetStats(): Promise<AdminStats> {
  const res = await request<{ ok: boolean; data: AdminStats }>('/api/admin/stats');
  return res.data;
}

// ─── Admin: Settings (gedeelde marge — zelfde bron als de prijsberekening) ────

export interface AdminSettings {
  default_margin_pct: number;
  /** v5 blok 6: vloer = AKP + deze minimummarge (0 = alleen AKP zelf). */
  min_margin_pct: number;
  /** v5 blok 6: kortingsplafond voor resellerkortingen. */
  max_discount_pct: number;
  /** V5.34: vast snijverlies-% in elke berekening — alleen Filip stelt dit in. */
  cutting_loss_pct?: number;
}

export async function adminGetSettings(): Promise<AdminSettings> {
  const res = await request<{ ok: boolean; data: AdminSettings }>('/api/admin/settings');
  return res.data;
}

export async function adminUpdateSettings(data: Partial<AdminSettings>): Promise<void> {
  // request() serialiseert de body zelf; hier nog eens JSON.stringify'en gaf een
  // dubbel verpakte body, waardoor de API elke instelling weigerde (400) en
  // wijzigingen aan marge, minimummarge, kortingsplafond en snijverlies stil
  // verloren gingen.
  await request('/api/admin/settings', { method: 'PUT', body: data });
}

// ─── Admin: Products ──────────────────────────────────────────────────────────

export interface AdminProduct extends ApiProduct {
  /** 1 = vast voorbeeld-product, niet verwijderbaar (zie migration 0019). */
  is_demo?: number;
  /** Afgesproken marge uit leveranciers-overeenkomst. NULL = geen afspraak. */
  target_margin_pct?: number | null;
  /** 1 = dumping toegestaan: prijsvloer (nooit onder inkoop) uitgeschakeld voor dit product. */
  allow_below_cost?: number;
  locked_price: number | null;
  serial_number: string | null;
  tech_specs: string | null;
  ean_code: string | null;
  supplier_name: string | null;
  supplier_country: string | null;
  /** Netto inkoopprijs (= list × (1 - discount/100)). Wat Filip écht betaalt. */
  supplier_cost: number | null;
  /** Bruto leverancier-listprijs. NULL = niet ingegeven (legacy data). */
  supplier_list_price?: number | null;
  /** Filip's korting bij die leverancier in %. NULL = niet ingegeven. */
  supplier_discount_pct?: number | null;
  primary_image: string | null;
}

export async function adminGetProducts(): Promise<AdminProduct[]> {
  const res = await request<{ ok: boolean; data: AdminProduct[] }>('/api/admin/products');
  return res.data;
}

export interface SupplierPriceHistoryEntry {
  id: string;
  list_price_eur: number | null;
  supplier_discount_pct: number | null;
  price_eur: number | null;
  changed_at: number;
  changed_by_email: string | null;
  reason: string | null;
}

export interface ProductPriceHistory {
  product: { id: string; name: string };
  current: { list_price_eur: number | null; supplier_discount_pct: number | null; price_eur: number | null } | null;
  history: SupplierPriceHistoryEntry[];
}

export async function adminGetProductHistory(id: string): Promise<ProductPriceHistory> {
  const res = await request<{ ok: boolean; data: ProductPriceHistory }>(`/api/admin/products/${id}/history`);
  return res.data;
}

// ─── R2 image upload (v3.2.58) ───────────────────────────────────────────
// Stuurt multipart/form-data naar /api/admin/upload — returnt een URL die in
// product_images, supplier.logo_url, etc. bewaard kan worden. Server-zijde
// validatie: image/* mime, max 8 MB, opslag in R2 met UUID-key.
export interface UploadResult {
  key: string;
  /** Path-relatieve URL die je rechtstreeks in <img src> kan zetten — wordt
   *  geserveerd via /api/uploads/r2/* op dezelfde Workers-host. */
  url: string;
  size: number;
  type: string;
}

export async function adminUploadImage(file: File, folder = 'products'): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('folder', folder);
  const token = getSessionToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api/admin/upload`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });
  const json = await res.json() as { ok: boolean; data?: UploadResult; error?: string };
  if (!res.ok || !json.ok) throw new Error(json.error || 'Upload mislukt');
  return json.data!;
}

/** Resolve een upload URL naar absolute URL voor <img src>. */
export function resolveUploadUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
}

/** Gezet wanneer de API na een inkoopwijziging de verkoopprijs automatisch
 *  heeft herberekend — zodat de UI dit aan Filip kan melden. */
export interface PriceRecalcInfo { old: number | null; new: number; marginPct: number }

export async function adminUpdateProduct(
  id: string,
  data: Record<string, unknown>
): Promise<{ product: AdminProduct; priceRecalculated: PriceRecalcInfo | null }> {
  const res = await request<{ ok: boolean; data: AdminProduct; price_recalculated?: PriceRecalcInfo | null }>(
    `/api/admin/products/${id}`,
    { method: 'PUT', body: data }
  );
  return { product: res.data, priceRecalculated: res.price_recalculated ?? null };
}

export interface AdminCreateProductPayload {
  slug: string;
  name: string;
  color: string;
  color_hex: string;
  texture: string;
  category: 'modern' | 'landelijk' | 'industrieel' | 'klassiek';
  dimensions: string;
  weight: string;
  description_nl: string;
  supplier_code: string;
  supplier_id?: string | null;
  sort_order?: number;
  locked_price?: number | null;
  serial_number?: string | null;
  tech_specs?: string | null;
  ean_code?: string | null;
  // Inkoop-blok bij creatie (zelfde decompositie als bij update):
  // wanneer list + discount meegestuurd worden, wordt cost server-side
  // herberekend als list × (1 - discount/100).
  supplier_list_price?:    number | null;
  supplier_discount_pct?:  number | null;
  supplier_cost?:          number | null;
  /** R2 upload URL voor productfoto (v3.2.58) — server INSERT-t als primary product_images. */
  image_url?:              string | null;
}

export async function adminCreateProduct(data: AdminCreateProductPayload): Promise<AdminProduct> {
  const res = await request<{ ok: boolean; data: AdminProduct }>('/api/admin/products', {
    method: 'POST',
    body: data,
  });
  return res.data;
}

// ─── Admin: Suppliers ─────────────────────────────────────────────────────────

export interface AdminSupplier {
  id: string;
  name: string;
  country: string;
  contact_email: string | null;
  is_active: number;
  btw_number: string | null;
  company_name: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  contact_person: string | null;
  contact_phone: string | null;
  website: string | null;
  notes: string | null;
  product_count: number;
  total_value: number;
  /** v3.2.58 — kleur voor visuele identificatie in tabellen/badges. */
  color_hex?: string | null;
}

export async function adminGetSuppliers(): Promise<AdminSupplier[]> {
  const res = await request<{ ok: boolean; data: AdminSupplier[] }>('/api/admin/suppliers');
  return res.data;
}

export async function adminCreateSupplier(data: Record<string, unknown>): Promise<AdminSupplier> {
  const res = await request<{ ok: boolean; data: AdminSupplier }>('/api/admin/suppliers', {
    method: 'POST',
    body: data,
  });
  return res.data;
}

export async function adminUpdateSupplier(id: string, data: Record<string, unknown>): Promise<AdminSupplier> {
  const res = await request<{ ok: boolean; data: AdminSupplier }>(`/api/admin/suppliers/${id}`, {
    method: 'PUT',
    body: data,
  });
  return res.data;
}

export async function adminDeleteSupplier(id: string): Promise<void> {
  await request(`/api/admin/suppliers/${id}`, { method: 'DELETE' });
}

// ─── Admin: Customers (users with role=customer) ──────────────────────────────

export interface AdminCustomer {
  id: string;
  email: string;
  name: string;
  is_active: number;
  created_at: number;
  quotes_count: number;
  total_spent: number;
  btw_number?: string | null;
  company_name?: string | null;
  /** v3.2.70 — role van de gebruiker. 'customer' is de meerderheid maar
   *  owners/resellers die testen verschijnen ook in deze lijst. UI kan
   *  de role als badge tonen om duidelijk te maken dat dit geen externe
   *  klant is maar een interne test-account. */
  role?: 'customer' | 'owner' | 'superadmin' | 'reseller' | 'supplier';
}

export async function adminGetCustomers(): Promise<AdminCustomer[]> {
  const res = await request<{ ok: boolean; data: AdminCustomer[] }>('/api/admin/customers');
  return res.data;
}

export interface AdminCustomerDetail extends AdminCustomer {
  /** v5.20: volledige klantgegevens voor het dossier. */
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  reseller_id?: string | null;
  reseller_company_name?: string | null;
  quotes: {
    id: string;
    status: string;
    total_excl_vat: number;
    total_incl_vat: number;
    created_at: number;
  }[];
}

export async function adminGetCustomer(id: string): Promise<AdminCustomerDetail> {
  const res = await request<{ ok: boolean; data: AdminCustomerDetail }>(`/api/admin/customers/${id}`);
  return res.data;
}

// ─── Admin: Quotes ────────────────────────────────────────────────────────────

export interface AdminQuote {
  id: string;
  user_id: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  address: string | null;
  status: string;
  total_excl_vat: number;
  total_incl_vat: number;
  vat_rate: number;
  is_b2b: number;
  customer_btw_number: string | null;
  notes_nl: string | null;
  expires_at: number | null;
  created_at: number;
  items_count: number;
  // reseller routing (NULL = Filip-direct)
  reseller_id: string | null;
  reseller_company_name?: string | null;
  reseller_btw_number?: string | null;
  reseller_address?: string | null;
  reseller_postal_code?: string | null;
  reseller_city?: string | null;
  reseller_logo_url?: string | null;
  reseller_contact_email?: string | null;
  reseller_contact_phone?: string | null;
  // werkadres / installatie-adres — null = zelfde als factuuradres (zie 0022).
  installation_address?:     string | null;
  installation_postal_code?: string | null;
  installation_city?:        string | null;
}

export interface AdminQuoteItem {
  id: string;
  quote_id: string;
  product_id: string;
  product_name: string;
  product_slug: string;
  qty_m2: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  sort_order: number;
}

export interface AdminQuoteDetail extends AdminQuote {
  items: AdminQuoteItem[];
}

export async function adminGetQuotes(): Promise<AdminQuote[]> {
  const res = await request<{ ok: boolean; data: AdminQuote[] }>('/api/admin/quotes');
  return res.data;
}

export async function adminGetQuoteDetail(id: string): Promise<AdminQuoteDetail> {
  const res = await request<{ ok: boolean; data: AdminQuoteDetail }>(`/api/admin/quotes/${id}`);
  return res.data;
}

export async function adminUpdateQuoteStatus(id: string, status: string): Promise<void> {
  await request(`/api/quotes/${id}/status`, {
    method: 'PUT',
    body: { status },
  });
}

/** Delete a quote. Only draft quotes can be deleted (server-enforced). */
export async function adminDeleteQuote(id: string): Promise<void> {
  await request(`/api/admin/quotes/${id}`, { method: 'DELETE' });
}

// ─── Superadmin: impersonate any user for testing ───────────────────────────

export interface TestAccount {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: number;
  reseller_company: string | null;
  reseller_type?: 'groothandel' | 'kleinhandel' | 'bouwonderneming' | 'installateur' | null;
  is_installateur?: number | null;
  quote_count: number;
}

export async function adminGetTestAccounts(): Promise<TestAccount[]> {
  const res = await request<{ ok: boolean; data: TestAccount[] }>('/api/admin/test-accounts');
  return res.data;
}

export async function adminImpersonate(email: string): Promise<{ token: string; user: ApiUser }> {
  const res = await request<{ ok: boolean; data: { token: string; user: ApiUser } }>(
    '/api/admin/impersonate',
    { method: 'POST', body: { email } }
  );
  return res.data;
}

// ─── Backlog / Audit / Backup ───────────────────────────────────────────────

export interface ActivityLogEntry {
  id: number;
  user_email: string;
  user_role: string | null;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface BerichtLogRegel {
  id: string; to_email: string; delivered_to: string | null; subject: string;
  template: string; status: string; error: string | null;
  mail_mode: string | null; context: string | null;
  sent_at: number | null; created_at: number;
}

export interface ResellerMedewerker {
  id: string; name: string; email: string; is_active: number;
  created_at: number; vestiging_id: string | null; vestiging_naam: string | null;
}

/** V6.24 — de handelaar beheert zijn eigen medewerkers (subaccounts). */
export async function resellerGetMembers(): Promise<ResellerMedewerker[]> {
  const res = await request<{ ok: boolean; data: ResellerMedewerker[] }>('/api/reseller/members');
  return res.data;
}

export async function resellerCreateMember(data: {
  name: string; email: string; password: string; vestiging_id?: string | null;
}): Promise<{ id: string }> {
  const res = await request<{ ok: boolean; data: { id: string } }>('/api/reseller/members', {
    method: 'POST', body: data,
  });
  return res.data;
}

export async function resellerUpdateMember(
  userId: string,
  data: { is_active?: 0 | 1; password?: string; vestiging_id?: string | null },
): Promise<void> {
  await request(`/api/reseller/members/${userId}`, { method: 'PUT', body: data });
}

export async function adminGetBerichtenlog(limit = 100): Promise<{ data: BerichtLogRegel[]; total: number }> {
  const res = await request<{ ok: boolean; data: BerichtLogRegel[]; total: number }>(
    `/api/admin/berichtenlog?limit=${limit}`
  );
  return { data: res.data, total: res.total };
}

export async function adminGetActivityLog(
  params?: { action?: string; user?: string; limit?: number; offset?: number }
): Promise<{ data: ActivityLogEntry[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.action) qs.set('action', params.action);
  if (params?.user)   qs.set('user', params.user);
  if (params?.limit)  qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const res = await request<{ ok: boolean; data: ActivityLogEntry[]; total: number }>(
    `/api/admin/activity-log${qs.toString() ? '?' + qs : ''}`
  );
  return { data: res.data, total: res.total };
}

export async function adminGetDeletedQuotes(): Promise<AdminQuote[]> {
  const res = await request<{ ok: boolean; data: AdminQuote[] }>('/api/admin/quotes/deleted');
  return res.data;
}

export async function adminRestoreQuote(id: string): Promise<void> {
  await request(`/api/admin/quotes/${id}/restore`, { method: 'POST' });
}

// ─── Contact form messages (admin readonly + status update) ─────────────────
export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  language: string;
  postal_code: string | null;
  reseller_id: string | null;
  reseller_name: string | null;
  status: string; // 'new' | 'read' | 'replied'
  created_at: number;
  updated_at: number;
}

export async function adminGetContactMessages(): Promise<ContactMessage[]> {
  const res = await request<{ ok: boolean; data: ContactMessage[] }>('/api/contact/admin/list');
  return res.data;
}

export async function adminUpdateContactMessageStatus(id: string, status: string): Promise<void> {
  await request(`/api/contact/admin/${id}/status`, { method: 'PUT', body: { status } });
}

/** Returns the URL to download a CSV export for the given resource. */
export function adminExportUrl(resource: 'quotes' | 'customers'): string {
  // Use absolute URL because the export endpoints stream a CSV body that
  // browsers can save directly when the link is clicked.
  return `${API_URL}/api/admin/export/${resource}`;
}

// ─── Admin: Pricing ───────────────────────────────────────────────────────────

export interface AdminPricingRow {
  id: string;
  name: string;
  slug: string;
  category: string;
  locked_price: number | null;
  bruto_price: number | null;
  discount_pct: number | null;
  rule_id: string | null;
}

export async function adminGetPricing(): Promise<AdminPricingRow[]> {
  const res = await request<{ ok: boolean; data: AdminPricingRow[] }>('/api/admin/pricing');
  return res.data;
}

export interface PricingRuleData {
  product_id?: string | null;
  category?: string | null;
  markup_pct?: number;
  min_order_m2?: number;
}

export async function adminCreatePricingRule(data: PricingRuleData): Promise<unknown> {
  const res = await request<{ ok: boolean; data: unknown }>('/api/pricing-rules', {
    method: 'POST',
    body: data,
  });
  return res.data;
}

export async function adminUpdatePricingRule(id: string, data: Partial<PricingRuleData>): Promise<unknown> {
  const res = await request<{ ok: boolean; data: unknown }>(`/api/pricing-rules/${id}`, {
    method: 'PUT',
    body: data,
  });
  return res.data;
}

// ─── Customer: My Quotes ──────────────────────────────────────────────────────

export async function fetchMyQuotes(): Promise<ApiQuote[]> {
  const res = await request<{ ok: boolean; data: ApiQuote[] }>('/api/quotes/my');
  return res.data;
}

// ─── Customer: Photos ─────────────────────────────────────────────────────────

export interface CustomerPhoto {
  id: string;
  user_id: string;
  photo_data: string;
  /** v3.2.59 — R2 URL wanneer aanwezig (nieuwe uploads). Frontend prefereert
   *  deze; valt terug op photo_data (base64) voor oude rijen. */
  photo_url?: string | null;
  caption: string | null;
  created_at: number;
}

export async function uploadPhotos(photos: { data: string; caption?: string }[]): Promise<{ id: string; caption: string | null }[]> {
  const res = await request<{ ok: boolean; data: { id: string; caption: string | null }[] }>('/api/uploads/photos', {
    method: 'POST',
    body: { photos },
  });
  return res.data;
}

/** v3.2.59 — R2-backed foto upload voor eindklant. Vervangt base64-flow voor
 *  performance. File → multipart → R2. Returns de saved-rij met photo_url. */
export async function uploadPhotoR2(file: File, caption?: string): Promise<{ id: string; url: string; caption: string | null }> {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  const token = getSessionToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api/uploads/photo-r2`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });
  const json = await res.json() as { ok: boolean; data?: { id: string; url: string; caption: string | null }; error?: string };
  if (!res.ok || !json.ok) throw new Error(json.error || 'Upload mislukt');
  return json.data!;
}

export async function fetchMyPhotos(userId: string): Promise<CustomerPhoto[]> {
  const res = await request<{ ok: boolean; data: CustomerPhoto[] }>(`/api/uploads/photos/${userId}`);
  return res.data;
}

export async function deletePhoto(photoId: string): Promise<void> {
  await request(`/api/uploads/photos/${photoId}`, { method: 'DELETE' });
}

// ─── Customer favorites ───────────────────────────────────────────────────

export interface FavoriteProduct {
  id: number;
  product_id: string;
  note: string | null;
  created_at: number;
  name: string;
  slug: string;
  color: string;
  color_hex: string;
  texture: string;
  category: string;
  dimensions: string;
  locked_price: number | null;
  is_remnant: number;
  remnant_price: number | null;
  primary_image: string | null;
}

export async function getMyFavorites(): Promise<FavoriteProduct[]> {
  const res = await request<{ ok: boolean; data: FavoriteProduct[] }>('/api/favorites/my');
  return res.data;
}

export async function addFavorite(productId: string, note?: string | null): Promise<void> {
  await request('/api/favorites', { method: 'POST', body: { product_id: productId, note: note ?? null } });
}

export async function removeFavorite(productId: string): Promise<void> {
  await request(`/api/favorites/${productId}`, { method: 'DELETE' });
}

// ─── Resellers (admin/owner-side) ──────────────────────────────────────────

// ─── Reseller contact + installer sub-types (stored as JSON) ────────────────
export interface ResellerContact {
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface ResellerInstaller {
  company_name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  btw?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  notes?: string | null;
}

export interface AdminReseller {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  slug: string;
  company_name: string;
  btw_number: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string;
  logo_url: string | null;
  default_discount_pct: number;
  /** Handelstype (v5 blok 2): groothandel | kleinhandel | bouwonderneming */
  type?: string;
  postcodes: string | null;
  notes: string | null;
  is_active: number;
  /** 1 = vast voorbeeld-reseller, niet verwijderbaar en nooit gekozen voor
   *  echte quote routing (zie migration 0019). */
  is_demo?: number;
  created_at: number;
  updated_at: number;
  quote_count?: number;
  customer_count?: number;
  // ─── Optional v3.2.7+ fields ────────────────────────────────────────────
  website?: string | null;
  // API returns these as raw JSON strings (TEXT columns). Helper below parses.
  contacts_json?: string | null;
  installers_json?: string | null;
  // ─── v3.2.55 — leverancier-koppelingen ─────────────────────────────────
  /** Lijst supplier_id's die deze reseller mag verdelen. Empty = alle
   *  leveranciers (legacy gedrag). Niet-leeg = strikt: enkel deze. */
  assigned_supplier_ids?: string[];
  /** v3.2.58 — kleur voor visuele identificatie in tabellen/badges. */
  color_hex?: string | null;
}

/** Parse the JSON arrays stored on AdminReseller — null on missing/invalid. */
export function parseResellerContacts(r: AdminReseller): ResellerContact[] {
  if (!r.contacts_json) return [];
  try { return JSON.parse(r.contacts_json) as ResellerContact[]; } catch { return []; }
}

export function parseResellerInstallers(r: AdminReseller): ResellerInstaller[] {
  if (!r.installers_json) return [];
  try { return JSON.parse(r.installers_json) as ResellerInstaller[]; } catch { return []; }
}

export interface ResellerPricingOverride {
  id: string;
  reseller_id: string;
  product_id: string;
  product_name: string;
  product_slug: string;
  override_price: number | null;
  override_discount_pct: number | null;
  /** Extra korting die op de basis-korting gestapeld wordt (additief). */
  extra_discount_pct?: number | null;
  /** 1 = product is gepauzeerd voor deze reseller (niet beschikbaar). */
  is_paused?: number;
}

export async function adminGetResellers(): Promise<AdminReseller[]> {
  const res = await request<{ ok: boolean; data: AdminReseller[] }>('/api/admin/resellers');
  return res.data;
}

export async function adminGetReseller(id: string): Promise<AdminReseller & { pricing_overrides: ResellerPricingOverride[] }> {
  const res = await request<{ ok: boolean; data: AdminReseller & { pricing_overrides: ResellerPricingOverride[] } }>(`/api/admin/resellers/${id}`);
  return res.data;
}

export interface CreateResellerPayload {
  email: string;
  name: string;
  company_name: string;
  slug: string;
  btw_number?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string;
  default_discount_pct?: number;
  /** Handelstype (v5 blok 2). */
  type?: 'groothandel' | 'kleinhandel' | 'bouwonderneming' | 'installateur';
  is_installateur?: boolean | number;
  postcodes?: string | null;
  notes?: string | null;
  /** Public website van de reseller (v3.2.7+). */
  website?: string | null;
  /** Extra contactpersonen van deze reseller. */
  contacts?: ResellerContact[];
  /** Installateurs onder deze reseller. */
  installers?: ResellerInstaller[];
  /** v3.2.55 — leverancier-koppeling. Empty = alle. */
  supplier_ids?: string[] | null;
  /** v3.2.58 — kleur voor visuele identificatie. */
  color_hex?: string | null;
}

export async function adminCreateReseller(payload: CreateResellerPayload): Promise<AdminReseller> {
  const res = await request<{ ok: boolean; data: AdminReseller }>('/api/admin/resellers', {
    method: 'POST',
    body: payload,
  });
  return res.data;
}

export async function adminUpdateReseller(id: string, payload: Partial<CreateResellerPayload>): Promise<AdminReseller> {
  const res = await request<{ ok: boolean; data: AdminReseller }>(`/api/admin/resellers/${id}`, {
    method: 'PUT',
    body: payload,
  });
  return res.data;
}

export async function adminDeleteReseller(id: string): Promise<void> {
  await request(`/api/admin/resellers/${id}`, { method: 'DELETE' });
}

export async function adminUpdateResellerPricing(
  resellerId: string,
  overrides: { product_id: string; override_price: number | null; override_discount_pct: number | null; extra_discount_pct?: number | null; is_paused?: number }[]
): Promise<void> {
  await request(`/api/admin/resellers/${resellerId}/pricing`, {
    method: 'PUT',
    body: { overrides },
  });
}

// ─── v5 blok 8: bestellingen & facturen ──────────────────────────────────────

export interface AdminOrder {
  id: string;
  order_number: number;
  quote_id: string | null;
  quote_number?: number | null;
  user_id: string | null;
  reseller_id: string | null;
  customer_name: string;
  customer_email: string;
  customer_btw_number: string | null;
  is_b2b: number;
  payment_terms: 'vooraf' | '30_dagen' | 'einde_maand';
  payment_status: 'open' | 'betaald';
  status: 'nieuw' | 'bevestigd' | 'geleverd' | 'geannuleerd';
  delivery_address: string | null;
  delivery_postal_code: string | null;
  delivery_city: string | null;
  billing_address: string | null;
  total_excl_vat: number;
  total_incl_vat: number;
  vat_rate: number;
  notes: string | null;
  invoice_count?: number;
  created_at: number;
  updated_at: number;
}

export interface AdminInvoice {
  id: string;
  invoice_number: number;
  order_id: string | null;
  order_number?: number | null;
  user_id: string | null;
  customer_name: string;
  customer_email: string;
  customer_btw_number: string | null;
  customer_address: string | null;
  total_excl_vat: number;
  vat_amount: number;
  total_incl_vat: number;
  vat_rate: number;
  due_date: number;
  terms_text: string | null;
  status: 'open' | 'betaald' | 'vervallen';
  created_at: number;
}

/** v5.18: admin maakt een nieuwe offerte met meerdere stenen (prijsvloer bewaakt). */
export async function adminCreateQuote(data: {
  customer_name: string; customer_email: string; customer_phone?: string | null;
  items: { product_id: string; qty_m2: number; unit_price?: number }[];
}): Promise<AdminQuote & { quote_number?: number }> {
  const res = await request<{ ok: boolean; data: AdminQuote & { quote_number?: number } }>('/api/admin/quotes', { method: 'POST', body: data });
  return res.data;
}

/** v5.18: creditnota voor een factuur (factuur zelf blijft onaantastbaar). */
export async function adminCreateCreditNote(invoiceId: string): Promise<AdminInvoice> {
  const res = await request<{ ok: boolean; data: AdminInvoice }>(`/api/admin/invoices/${invoiceId}/credit-note`, { method: 'POST', body: {} });
  return res.data;
}

/** v5.17: admin bewerkt een offerte (tekst, klantgegevens, bedrag) — met audit. */
export async function adminEditQuote(quoteId: string, data: Record<string, unknown>): Promise<AdminQuote> {
  const res = await request<{ ok: boolean; data: AdminQuote }>(`/api/admin/quotes/${quoteId}`, { method: 'PUT', body: data });
  return res.data;
}

export async function adminConvertQuoteToOrder(quoteId: string, payload?: Record<string, unknown>): Promise<AdminOrder> {
  const res = await request<{ ok: boolean; data: AdminOrder }>(`/api/admin/quotes/${quoteId}/convert-to-order`, { method: 'POST', body: payload ?? {} });
  return res.data;
}
export async function adminGetOrders(): Promise<AdminOrder[]> {
  const res = await request<{ ok: boolean; data: AdminOrder[] }>('/api/admin/orders');
  return res.data;
}
export async function adminUpdateOrder(id: string, data: Record<string, unknown>): Promise<AdminOrder> {
  const res = await request<{ ok: boolean; data: AdminOrder }>(`/api/admin/orders/${id}`, { method: 'PUT', body: data });
  return res.data;
}
export async function adminCreateInvoice(orderId: string): Promise<AdminInvoice> {
  const res = await request<{ ok: boolean; data: AdminInvoice }>(`/api/admin/orders/${orderId}/invoice`, { method: 'POST', body: {} });
  return res.data;
}
export async function adminGetInvoices(): Promise<AdminInvoice[]> {
  const res = await request<{ ok: boolean; data: AdminInvoice[] }>('/api/admin/invoices');
  return res.data;
}
export async function adminSetInvoiceStatus(id: string, status: 'open' | 'betaald' | 'vervallen'): Promise<void> {
  await request(`/api/admin/invoices/${id}/status`, { method: 'PUT', body: { status } });
}
export async function fetchMyOrders(): Promise<AdminOrder[]> {
  const res = await request<{ ok: boolean; data: AdminOrder[] }>('/api/my/orders');
  return res.data;
}
export async function fetchMyInvoices(): Promise<AdminInvoice[]> {
  const res = await request<{ ok: boolean; data: AdminInvoice[] }>('/api/my/invoices');
  return res.data;
}

// ─── Resellers (reseller's own dashboard) ──────────────────────────────────

export interface ResellerProfile {
  id: string;
  user_id: string;
  slug: string;
  company_name: string;
  btw_number: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string;
  logo_url: string | null;
  default_discount_pct: number;
  postcodes: string | null;
  notes: string | null;
  is_active: number;
  installers_json?: string | null;
}

// v5.19: reseller-kant bestellingen, facturen & creditnota's (eigen scope)
export async function resellerConvertQuoteToOrder(quoteId: string): Promise<AdminOrder> {
  const res = await request<{ ok: boolean; data: AdminOrder }>(`/api/reseller/quotes/${quoteId}/convert-to-order`, { method: 'POST', body: {} });
  return res.data;
}
export async function resellerGetOrders(): Promise<AdminOrder[]> {
  const res = await request<{ ok: boolean; data: AdminOrder[] }>('/api/reseller/orders');
  return res.data;
}
export async function resellerUpdateOrder(id: string, data: Record<string, unknown>): Promise<AdminOrder> {
  const res = await request<{ ok: boolean; data: AdminOrder }>(`/api/reseller/orders/${id}`, { method: 'PUT', body: data });
  return res.data;
}
export async function resellerCreateInvoice(orderId: string): Promise<AdminInvoice> {
  const res = await request<{ ok: boolean; data: AdminInvoice }>(`/api/reseller/orders/${orderId}/invoice`, { method: 'POST', body: {} });
  return res.data;
}
export async function resellerGetInvoices(): Promise<AdminInvoice[]> {
  const res = await request<{ ok: boolean; data: AdminInvoice[] }>('/api/reseller/invoices');
  return res.data;
}
export async function resellerCreateCreditNote(invoiceId: string): Promise<AdminInvoice> {
  const res = await request<{ ok: boolean; data: AdminInvoice }>(`/api/reseller/invoices/${invoiceId}/credit-note`, { method: 'POST', body: {} });
  return res.data;
}

/** v5 blok 5: offerte van een eindklant overnemen op naam van de reseller. */
export async function resellerClaimQuote(quoteId: string): Promise<void> {
  await request(`/api/reseller/quotes/${encodeURIComponent(quoteId)}/claim`, { method: 'POST' });
}

export async function resellerGetProfile(): Promise<ResellerProfile> {
  const res = await request<{ ok: boolean; data: ResellerProfile }>('/api/reseller/profile');
  return res.data;
}

export interface ResellerSelfUpdate {
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  notes?: string | null;
  /** v5 blok 7: eigen logo — komt op de geprinte offerte van de reseller. */
  logo_url?: string | null;
  website?: string | null;
}

export async function resellerUpdateProfile(payload: ResellerSelfUpdate): Promise<ResellerProfile> {
  const res = await request<{ ok: boolean; data: ResellerProfile }>('/api/reseller/profile', {
    method: 'PUT',
    body: payload,
  });
  return res.data;
}

/** Reseller-scoped catalog (no supplier_id ever in response). */
export interface ResellerProduct {
  id: string;
  name: string;
  slug: string;
  color: string;
  color_hex: string;
  texture: string;
  category: string;
  dimensions: string;
  weight: string;
  description: string;
  catalog_price: number;
  effective_price: number;
  is_remnant: number;
  remnant_price: number | null;
  remnant_qty_m2: number | null;
  remnant_note: string | null;
}

export async function resellerGetProducts(): Promise<ResellerProduct[]> {
  const res = await request<{ ok: boolean; data: ResellerProduct[] }>('/api/reseller/products');
  return res.data;
}

export interface ResellerCustomer {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  company_name: string | null;
  btw_number: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  phone: string | null;
  reseller_id: string | null;
  quote_count: number;
  created_at: number;
}

export async function resellerGetCustomers(): Promise<ResellerCustomer[]> {
  const res = await request<{ ok: boolean; data: ResellerCustomer[] }>('/api/reseller/customers');
  return res.data;
}

export interface ResellerCreateCustomerPayload {
  name: string;
  email: string;
  phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  company_name?: string | null;
  btw_number?: string | null;
}

export async function resellerCreateCustomer(payload: ResellerCreateCustomerPayload): Promise<ResellerCustomer> {
  const res = await request<{ ok: boolean; data: ResellerCustomer }>('/api/reseller/customers', {
    method: 'POST',
    body: payload,
  });
  return res.data;
}

export interface ResellerQuote {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  status: string;
  total_excl_vat: number;
  total_incl_vat: number;
  is_b2b: number;
  customer_btw_number: string | null;
  notes_nl: string | null;
  items_count: number;
  created_at: number;
  expires_at: number | null;
  // Werkadres — null = zelfde als factuuradres.
  installation_address?:     string | null;
  installation_postal_code?: string | null;
  installation_city?:        string | null;
}

export async function resellerGetQuotes(): Promise<ResellerQuote[]> {
  const res = await request<{ ok: boolean; data: ResellerQuote[] }>('/api/reseller/quotes');
  return res.data;
}

export async function resellerUpdateQuoteStatus(id: string, status: string): Promise<void> {
  await request(`/api/reseller/quotes/${id}/status`, { method: 'PUT', body: { status } });
}

export interface ResellerCreateQuotePayload {
  customer_name:        string;
  customer_email:       string;
  customer_phone?:      string | null;
  customer_postal_code?: string | null;
  address?:             string | null;
  installation_address?:     string | null;
  installation_postal_code?: string | null;
  installation_city?:        string | null;
  is_b2b?:              boolean;
  customer_btw_number?: string | null;
  notes?:               string | null;
  items:                {
    product_id: string; qty_m2: number;
    // V5.34: toebehoren per steen — server rekent alles met Filips prijzen.
    joint_width_mm?: number; corner_count?: number;
    include_glue?: boolean; include_grout?: boolean; grout_color?: string;
  }[];
}

export async function resellerCreateQuote(payload: ResellerCreateQuotePayload): Promise<{ id: string }> {
  const res = await request<{ ok: boolean; data: { id: string } }>('/api/reseller/quotes', {
    method: 'POST',
    body: payload,
  });
  return res.data;
}

// ════════════════════════════════════════════════════════════════════════════
// V5.31 — reseller-zelfregistratie (uitnodiging → invullen → goedkeuren)
// + subaccounts (medewerkers), beheerd door Filip
// ════════════════════════════════════════════════════════════════════════════

export interface ResellerApplication {
  id: string;
  token: string;
  email: string;
  status: 'open' | 'submitted' | 'approved' | 'rejected';
  company_name: string | null;
  contact_name: string | null;
  btw_number: string | null;
  btw_valid: number | null;
  contact_phone: string | null;
  contact_email: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  website: string | null;
  type: 'groothandel' | 'kleinhandel' | 'bouwonderneming' | 'installateur' | null;
  is_installateur?: number;
  notes: string | null;
  created_at: number;
  submitted_at: number | null;
  reviewed_at: number | null;
}

export async function adminCreateResellerInvite(email: string, type?: string): Promise<{ token: string; path: string }> {
  const res = await request<{ ok: boolean; data: { token: string; path: string } }>('/api/admin/reseller-invites', {
    method: 'POST', body: { email, ...(type ? { type } : {}) },
  });
  return res.data;
}

export async function adminGetResellerApplications(): Promise<ResellerApplication[]> {
  const res = await request<{ ok: boolean; data: ResellerApplication[] }>('/api/admin/reseller-aanvragen');
  return res.data;
}

export async function adminApproveResellerApplication(
  id: string,
  payload: { default_discount_pct: number; type: string; initial_password?: string },
): Promise<void> {
  await request(`/api/admin/reseller-aanvragen/${id}/approve`, { method: 'POST', body: payload });
}

export async function adminRejectResellerApplication(id: string): Promise<void> {
  await request(`/api/admin/reseller-aanvragen/${id}/reject`, { method: 'POST' });
}

export interface ResellerMember {
  id: string;
  name: string;
  email: string;
  is_active: number;
  login_method: string;
  created_at: number;
  vestiging_id?: string | null;
  vestiging_naam?: string | null;
}

// V6 punt 21 — vestigingen per groothandel
export interface ResellerVestiging {
  id: string;
  reseller_id: string;
  naam: string;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: number;
  member_count?: number;
  quote_count?: number;
}

export async function adminGetVestigingen(resellerId: string): Promise<ResellerVestiging[]> {
  const res = await request<{ ok: boolean; data: ResellerVestiging[] }>(`/api/admin/resellers/${resellerId}/vestigingen`);
  return res.data;
}
export async function adminCreateVestiging(resellerId: string, payload: Partial<ResellerVestiging> & { naam: string }): Promise<void> {
  await request(`/api/admin/resellers/${resellerId}/vestigingen`, { method: 'POST', body: payload });
}
export async function adminUpdateVestiging(vestigingId: string, payload: Partial<ResellerVestiging>): Promise<void> {
  await request(`/api/admin/vestigingen/${vestigingId}`, { method: 'PUT', body: payload });
}

// V6 punt 24 — zelf-aanmelding zonder uitnodiging
export async function selfApplyReseller(email: string): Promise<{ token: string; bestaand?: boolean }> {
  const res = await request<{ ok: boolean; data: { token: string; bestaand?: boolean } }>(
    '/api/reseller-aanvraag/zelf', { method: 'POST', body: { email } });
  return res.data;
}

export async function adminGetResellerMembers(resellerId: string): Promise<ResellerMember[]> {
  const res = await request<{ ok: boolean; data: ResellerMember[] }>(`/api/admin/resellers/${resellerId}/members`);
  return res.data;
}

export async function adminCreateResellerMember(resellerId: string, payload: { name: string; email: string; password: string; vestiging_id?: string | null }): Promise<void> {
  await request(`/api/admin/resellers/${resellerId}/members`, { method: 'POST', body: payload });
}

export async function adminUpdateResellerMember(userId: string, payload: { is_active?: 0 | 1; password?: string; vestiging_id?: string | null }): Promise<void> {
  await request(`/api/admin/reseller-members/${userId}`, { method: 'PUT', body: payload });
}

// Publiek (token-link, geen login):
export async function fetchResellerApplication(token: string): Promise<Omit<ResellerApplication, 'id'>> {
  const res = await request<{ ok: boolean; data: Omit<ResellerApplication, 'id'> }>(`/api/reseller-aanvraag/${token}`);
  return res.data;
}

export async function saveResellerApplication(token: string, fields: Record<string, string>): Promise<void> {
  await request(`/api/reseller-aanvraag/${token}`, { method: 'PUT', body: fields });
}

export async function submitResellerApplication(token: string): Promise<void> {
  await request(`/api/reseller-aanvraag/${token}/indienen`, { method: 'POST' });
}

// ════════════════════════════════════════════════════════════════════════════
// V5.32 — restpartij-berichten (interesse + voorstellen), onwisbaar
// ════════════════════════════════════════════════════════════════════════════

export interface RestpartijBericht {
  id: string;
  kind: 'interesse' | 'voorstel';
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  message: string;
  product_id: string | null;
  product_name: string | null;
  remnant_price: number | null;
  status: 'nieuw' | 'behandeld';
  created_at: number;
}

export async function sendRestpartijBericht(payload: {
  kind: 'interesse' | 'voorstel'; name: string; email: string; phone?: string;
  company?: string; message: string; product_id?: string; product_name?: string; remnant_price?: number;
}): Promise<void> {
  await request('/api/restpartijen/berichten', { method: 'POST', body: payload });
}

export async function adminGetRestpartijBerichten(): Promise<RestpartijBericht[]> {
  const res = await request<{ ok: boolean; data: RestpartijBericht[] }>('/api/admin/restpartijen/berichten');
  return res.data;
}

export async function adminSetRestpartijBerichtStatus(id: string, status: 'nieuw' | 'behandeld'): Promise<void> {
  await request(`/api/admin/restpartijen/berichten/${id}/status`, { method: 'PUT', body: { status } });
}


// ════════════════════════════════════════════════════════════════════════════
// V6 punten 18-20 — opvolging & forecast
// ════════════════════════════════════════════════════════════════════════════

export interface OpvolgingData {
  vakken: Record<'3m' | '6m' | '1j' | 'meer', { count: number; total: number }>;
  rows: Array<{
    id: string; quote_number: number; customer_name: string; customer_email: string;
    total_incl_vat: number; project_termijn: string; opvolg_status: string;
    opvolg_mail_sent_at: number | null; created_at: number; status: string;
  }>;
}

export async function adminGetOpvolging(): Promise<OpvolgingData> {
  const res = await request<{ ok: boolean; data: OpvolgingData }>('/api/admin/opvolging');
  return res.data;
}

export async function adminSetOpvolging(quoteId: string, payload: { project_termijn?: string; opvolg_status?: string }): Promise<void> {
  await request(`/api/admin/opvolging/${quoteId}`, { method: 'PUT', body: payload });
}

export async function adminRunOpvolgSweep(): Promise<number> {
  const res = await request<{ ok: boolean; data: { verstuurd: number } }>('/api/admin/opvolging/sweep', { method: 'POST' });
  return res.data.verstuurd;
}
